import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { compose } from 'redux';
import EmailIcon from '@mui/icons-material/Email';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AppButton from '../../../../components/app-button';
import RegisterHeader from '../../../../components/headers/register-header';
import Header1 from '../../../../components/headers/header1';
import Header2 from '../../../../components/headers/header2';
import { Column, Row } from '../../../../components/layout';
import AppSpinner from '../../../../components/app-spinner';
import NewErrorMessage from '../../../../components/new-error-message';
import { Icons } from '../../../../util/icons';
import colors from '../../../../util/colors';
import * as fonts from '../../../../util/fonts';
import { strings } from '../../../../util/strings';
import { censorPhone, censorEmail } from '../../../../util/utility';
import { post } from '../../../../api/api';
import { uriRequestActivationCode } from '../../../../api/endpoints';
import { addToWizard } from '../../../../store/actions';
import './styles.scss';

interface DataConfirmationProps {
  labels: typeof strings.pt.register;
  messageLabels: typeof strings.pt.messages;
  wizard: any;
  globalStyles: any;
  type?: 'sms' | 'email';
  addToWizard?: (value: any) => void;
}

function DataConfirmation({
  labels,
  messageLabels,
  wizard,
  globalStyles,
  type: propsType,
  addToWizard,
}: DataConfirmationProps) {
  const history = useHistory();
  const { type: paramType } = useParams<{ type: string }>();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mode = propsType || paramType || 'sms';
  const isEmail = mode === 'email';

  const primaryColor = globalStyles?.primaryColor || colors.blue;

  const contactData = isEmail
    ? wizard?.dataValidation?.currentUser?.email || 'exemplo@email.com'
    : wizard?.dataValidation?.currentUser?.phone || '';

  const censoredData = isEmail
    ? (contactData.includes('*') ? contactData : censorEmail(contactData))
    : (contactData.includes('*') ? contactData : censorPhone(contactData));

  const title =
    labels?.register_email_confirmation_title ||
    strings.pt.register.register_email_confirmation_title;

  const buttonLabel = isEmail
    ? (labels as any)?.register_email_confirmation_sendCode || strings.pt.register.register_email_activation_sendCode
    : labels?.register_phone_activation_sendCode;

  const handleSendCode = async () => {
    setErrorMessage(null);

    const sessionId =
      wizard?.dataValidation?.currentUser?.sessionId ||
      wizard?.activateRegistration?.guid ||
      '';

    const fullName = wizard?.insuranceValidation?.name || '';

    const payload = {
      sessionId: sessionId,
      senderType: isEmail ? 'EMAIL' : 'SMS',
      name: fullName,
    };

    if (addToWizard) {
      addToWizard({
        dataValidation: {
          ...wizard?.dataValidation,
          validationType: isEmail ? 'EMAIL' : 'SMS',
          useSessionPhone: !isEmail,
          useSessionEmail: isEmail,
          isNewEmail: false,
        },
      });
    }

    try {
      setLoading(true);
      const res: any = await post(
        uriRequestActivationCode(),
        JSON.stringify(payload),
        false,
        'Register-DataConfirmation'
      );

      setLoading(false);

      if (res?.result?.status === 'Success' || res?.status === 200 || !res?.error) {
        history.push(isEmail ? '/codigo-validacao/email' : '/codigo-validacao/sms');
      } else {
        setErrorMessage(
          res?.result?.message ||
          messageLabels?.general_errorMessage ||
          'Ocorreu um erro ao enviar o código de validação.'
        );
      }
    } catch (error: any) {
      setLoading(false);
      const errorMsg =
        error?.response?.data?.result?.message ||
        error?.message ||
        messageLabels?.general_errorMessage ||
        'Ocorreu um erro ao enviar o código de validação.';
      setErrorMessage(errorMsg);
    }
  };

  return (
    <Column className="register-main-column">
      <RegisterHeader />
      <Column className="register-container" style={{ alignItems: 'center' }}>
        <Row
          className="register-header-row"
          style={{ justifyContent: 'center', marginTop: '2.5rem' }}
        >
          <Header1
            style={{
              ...fonts.XL,
              ...fonts.Bold,
              color: colors.black,
              textAlign: 'center',
            }}
          >
            {title}
          </Header1>
        </Row>

        <Column
          className="register-existing-user-text"
          style={{
            ...fonts.ML,
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '380px',
            width: '100%',
          }}
        >
          <Header2
            style={{
              ...fonts.M,
              marginBottom: '2.5rem',
              textAlign: 'center',
              color: colors.darkGrey,
              lineHeight: '1.4',
            }}
          >
            {labels?.register_phone_activation_subtitle}
          </Header2>

          {errorMessage && (
            <Row
              style={{
                width: '100%',
                marginBottom: '1.5rem',
                justifyContent: 'center',
              }}
            >
              <NewErrorMessage
                className=""
                iconName={Icons.ALERT_PROBLEM}
                colorIcon={colors.alertRed}
                text={errorMessage}
              />
            </Row>
          )}

          <Row
            style={{
              alignItems: 'center',
              marginBottom: '3rem',
              width: '100%',
              justifyContent: 'center',
            }}
          >
            {}
            {isEmail ? (
              <EmailIcon style={{ fontSize: '48px', color: primaryColor, marginRight: '20px' }} />
            ) : (
              <PhoneAndroidIcon style={{ fontSize: '48px', color: primaryColor, marginRight: '20px' }} />
            )}

            <Column style={{ alignItems: 'flex-start', textAlign: 'left' }}>
              <span style={{ ...fonts.M, ...fonts.Bold }}>
                {isEmail
                  ? labels?.register_email_activation_email
                  : labels?.register_phone_activation_sms}
              </span>
              <span style={{ ...fonts.M, color: colors.darkGrey }}>
                {labels?.register_phone_activation_sendTo}
              </span>
              <span style={{ ...fonts.M, ...fonts.Bold }}>{censoredData}</span>
            </Column>
          </Row>

          <AppButton
            onClick={handleSendCode}
            disabled={loading}
            style={{
              ...fonts.M,
              ...fonts.Bold,
              width: '100%',
              maxWidth: '350px',
              borderRadius: '25px',
              height: '50px',
              backgroundColor: primaryColor,
              color: colors.white,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading
              ? labels?.register_phone_activation_sending || 'A enviar...'
              : buttonLabel}
          </AppButton>

          <Row
            style={{
              marginTop: '2rem',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ flex: 1, height: '1px', backgroundColor: colors.placeholderGrey }} />
            <span style={{ margin: '0 10px', ...fonts.S, color: colors.darkGrey }}>
              {labels?.register_phone_activation_or}
            </span>
            <div style={{ flex: 1, height: '1px', backgroundColor: colors.placeholderGrey }} />
          </Row>

          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              const hasPhone = Boolean(wizard?.dataValidation?.currentUser?.phone);
              const hasEmail = Boolean(wizard?.dataValidation?.currentUser?.email);

              if (!isEmail && hasPhone && hasEmail) {
                history.push('/confirmacao-dados/email');
              } else {
                history.push('/contacto-alternativo');
              }
            }}
            style={{
              ...fonts.M,
              ...fonts.Regular,
              color: primaryColor,
              marginTop: '1rem',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {labels?.register_phone_activation_otherVal}
          </a>
        </Column>
      </Column>
    </Column>
  );
}

const mapStateToProps = ({ stringsDefault, wizard, globalStyles }: any) => ({
  labels: stringsDefault.value.pt.register,
  messageLabels: stringsDefault.value.pt.messages,
  wizard: wizard.value,
  globalStyles: globalStyles.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(DataConfirmation);
